import BigNumber from 'bignumber.js'
import { shallow } from 'zustand/shallow'
import { useMemo, useEffect } from 'react'
import createDependableResource from 'utils/zustand/createDependableResource'
import createPeriodicRefresher from 'utils/createPeriodicRefresher'
import { getAccount, onAccountInitOrChange } from 'state/web3/account'
import { BIG_ZERO } from 'utils/bigNumber'
import { multicallv2 } from 'utils/multicall'
import crc20Abi from 'config/abi/erc20.json'
import type { AwaitedReturnType } from 'utils/types'
import { FetchStatus, UseTokenBalanceState } from 'state/tokens/types'
import type { Erc20 } from '../../../types/ethers-contracts'

type TokenAddr = string
const mountedTokens: Record<TokenAddr, number> = {}
const emptyTokensUserBalance = {}

export const useTokensUserBalanceResource = createDependableResource<Record<TokenAddr, BigNumber>>({
  initValue: emptyTokensUserBalance,
  getValue: async () => {
    const account = getAccount()
    if (!account) {
      return emptyTokensUserBalance
    }
    const fetchingTokens = Object.entries(mountedTokens).filter(([_tokenAddr, mounted]) => mounted > 0)
    const calls = fetchingTokens.map(([tokenAddr]) => ({
      address: tokenAddr,
      name: 'balanceOf',
      params: [account],
    }))

    const balanceOfResponses = (await multicallv2(crc20Abi, calls)) as AwaitedReturnType<Erc20['balanceOf']>[]

    const newBalances = Object.fromEntries(
      fetchingTokens.map(([tokenAddr], index) => {
        return [tokenAddr, new BigNumber(balanceOfResponses[index].toString())]
      }),
    )

    return {
      ...useTokensUserBalanceResource.getState().value,
      ...newBalances,
    }
  },
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dependResources: [],
  dbgName: 'TokensUserBalance',
})

export const useTokensUserBalancePeriodicFetching = createPeriodicRefresher(
  async () => {
    await useTokensUserBalanceResource.getState().refresh()
  },
  {
    frequency: 'fast',
    dbgName: 'TokensUserBalancePeriodicFetching',
  },
)

export const useTokenBalance = (tokenAddress: string): UseTokenBalanceState => {
  useTokensUserBalancePeriodicFetching()

  useEffect(() => {
    if (mountedTokens[tokenAddress] === undefined) {
      mountedTokens[tokenAddress] = 0
    }
    mountedTokens[tokenAddress] += 1
    if (mountedTokens[tokenAddress] === 1) {
      useTokensUserBalanceResource.getState().refresh()
    }

    return () => {
      mountedTokens[tokenAddress] -= 1
    }
  }, [tokenAddress])

  const tokenBalanceResource = useTokensUserBalanceResource((s) => [s.value[tokenAddress], s.error], shallow)

  return useMemo(() => {
    const [balance, error] = tokenBalanceResource

    if (balance) {
      return {
        balance,
        fetchStatus: FetchStatus.SUCCESS,
      }
    }
    if (error) {
      return {
        balance: BIG_ZERO,
        fetchStatus: FetchStatus.FAILED,
      }
    }
    return {
      balance: BIG_ZERO,
      fetchStatus: FetchStatus.NOT_FETCHED,
    }
  }, [tokenBalanceResource])
}
